import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layouts/MainLayout';
import { deviceStore } from '@/stores/deviceStore';
import { DashboardSummary } from '@/components/dashboard/DashboardSummary';
import { DeviceGrid } from '@/components/dashboard/DeviceGrid';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Download, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// Define BeforeInstallPromptEvent interface if not already defined
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Define enhanced Navigator interface that includes standalone property
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

const DashboardPage = observer(() => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Add state for PWA installation
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [pwaStatus, setPwaStatus] = useState<string>('');
  const [showPwaDebug, setShowPwaDebug] = useState(import.meta.env.VITE_DEV); // Only show in development by default
  
  useEffect(() => {
    // Load devices when page loads
    loadDevices();
    
    // Set up interval to refresh summary data
    const summaryInterval = setInterval(() => {
      deviceStore.loadSummary();
    }, 30000); // every 30 seconds
    
    // Connect to SSE for real-time updates
    deviceStore.connectToSSE();
    
    // Connect/disconnect SSE when page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        deviceStore.connectToSSE();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Check if the app is running as installed PWA
    const isInstalledAsPWA = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             (window.navigator as NavigatorWithStandalone).standalone || 
             document.referrer.includes('android-app://');
    };

    if (isInstalledAsPWA()) {
      setPwaStatus('App is running as an installed PWA');
    } else {
      setPwaStatus('App is running in browser mode');
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('Dashboard - beforeinstallprompt event captured');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Cleanup
    return () => {
      clearInterval(summaryInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      deviceStore.cleanup();
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  const loadDevices = async () => {
    try {
      await deviceStore.loadDevices();
      await deviceStore.loadSummary();
      
      toast({
        description: t('dashboard.notifications.devicesLoaded'),
        duration: 1000, // Close after 3 seconds
      });
    } catch (error) {
      console.error('Error loading devices:', error);
      
      toast({
        variant: "destructive",
        description: t('dashboard.notifications.loadingFailed'),
        duration: 1000, // Close after 5 seconds
      });
    }
  };
  
  // Function to handle PWA installation
  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      alert('Installation prompt not available. This could happen if:\n- The app is already installed\n- The browser doesn\'t support PWA installation\n- The site doesn\'t meet PWA criteria (proper manifest, https, etc.)');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User ${outcome} the installation`);
      
      if (outcome === 'accepted') {
        setPwaStatus('App was successfully installed!');
      } else {
        setPwaStatus('App installation was dismissed by user');
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('Installation failed:', error);
      setPwaStatus(`Installation failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // Check PWA requirements
  const checkPwaRequirements = () => {
    const results = [];
    
    // Check HTTPS
    if (window.location.protocol === 'https:' || window.location.hostname === 'localhost') {
      results.push('✅ Running on HTTPS or localhost');
    } else {
      results.push('❌ Not running on HTTPS (required for PWA)');
    }
    
    // Check service worker
    if ('serviceWorker' in navigator) {
      results.push('✅ Service Worker API supported');
    } else {
      results.push('❌ Service Worker not supported');
    }
    
    // Check manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      results.push('✅ Web manifest is linked');
    } else {
      results.push('❌ No web manifest found');
    }
    
    // Check icons
    const hasIcons = document.querySelector('link[rel="apple-touch-icon"]') || 
                     document.querySelector('link[rel="icon"]');
    if (hasIcons) {
      results.push('✅ Icons are defined');
    } else {
      results.push('❌ No icons found');
    }
    
    return results;
  };

  return (
    <MainLayout>
      <div className="space-y-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('dashboard.title')}
          </h1>
          <p className="text-gray-600">
            {t('dashboard.subtitle')}
          </p>
        </div>
        
        <DashboardSummary />
        
        <div className="space-y-6">
          <h2 className="text-2xl font-bold">{t('dashboard.deviceOverview')}</h2>
          <DeviceGrid />
        </div>
        
        {/* PWA Debug Section - only shown in development or if explicitly toggled */}
        {showPwaDebug && (
          <div className="mt-8 p-4 border rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">PWA Installation Debug</h2>
              {import.meta.env.VITE_PROD && (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPwaDebug(false)}
                >
                  Close Debug Panel
                </Button>
              )}
            </div>
            
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Current Status</AlertTitle>
              <AlertDescription>{pwaStatus || 'Checking PWA status...'}</AlertDescription>
            </Alert>
            
            <div className="mb-4">
              <h3 className="font-medium mb-2">PWA Requirements Check:</h3>
              <ul className="space-y-1 text-sm">
                {checkPwaRequirements().map((result) => (
                  <li key={result}>{result}</li>
                ))}
              </ul>
            </div>
            
            <div className="flex gap-4 mt-4">
              <Button
                variant="default"
                onClick={handleInstallClick}
                disabled={!isInstallable}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Install App
              </Button>
              
              <Button
                variant="outline"
                onClick={() => {
                  // Force refresh the page - this sometimes helps trigger the install prompt
                  window.location.reload();
                }}
              >
                Refresh Page
              </Button>
              
              {import.meta.env.VITE_PROD && (
                <Button
                  variant="outline"
                  onClick={() => {
                    localStorage.setItem('force_pwa_debug', 'true');
                    setShowPwaDebug(true);
                  }}
                >
                  Enable Debug Panel
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
});

export default DashboardPage; 